import { isExpiredJwtAuthFailure } from '../../../../common-resources/packages/auth-utils/src/index';

import type { ApiErrorDocument } from '../contracts/apiContract';
import type {
  HttpClient,
  HttpClientOfflineCacheAdapter,
  HttpClientOfflineCacheReadResult,
  HttpClientOfflineCacheResolvedScope,
  HttpClientRequest,
} from './httpClient';

export class BrowserHttpError extends Error {
  statusCode: number;
  errorDocument?: ApiErrorDocument;
  requestMetadata?: BrowserHttpRequestMetadata;

  constructor(
    message: string,
    statusCode: number,
    errorDocument?: ApiErrorDocument,
    requestMetadata?: BrowserHttpRequestMetadata,
  ) {
    super(message);
    this.name = 'BrowserHttpError';
    this.statusCode = statusCode;
    this.errorDocument = errorDocument;
    this.requestMetadata = requestMetadata;
  }
}

export type NetworkUnavailableReason =
  | 'browser_offline'
  | 'fetch_failed'
  | 'timeout';

export class NetworkUnavailableError extends Error {
  reason: NetworkUnavailableReason;
  cause?: unknown;
  requestMetadata?: BrowserHttpRequestMetadata;

  constructor(
    message = 'Cannot verify your session while offline. Reconnect to continue.',
    reason: NetworkUnavailableReason = 'fetch_failed',
    cause?: unknown,
    requestMetadata?: BrowserHttpRequestMetadata,
  ) {
    super(message);
    this.name = 'NetworkUnavailableError';
    this.reason = reason;
    this.cause = cause;
    this.requestMetadata = requestMetadata;
  }
}

export interface BrowserHttpRequestMetadata {
  requestId: string;
  idempotencyKey?: string;
}

export interface BrowserHttpClientOptions {
  baseUrl: string;
  requestTimeoutMs?: number;
  offlineCacheAdapter?: HttpClientOfflineCacheAdapter;
  beforeRequest?: (input: {
    request: HttpClientRequest;
    url: string;
    requestMetadata: BrowserHttpRequestMetadata;
  }) => Promise<void> | void;
  defaultHeaders?:
    | Record<string, string>
    | ((request: HttpClientRequest) => Record<string, string>);
  fetchImplementation?: typeof fetch;
  generateRequestId?: () => string;
  generateIdempotencyKey?: () => string;
  onUnauthorized?: (input: {
    request: HttpClientRequest;
    statusCode: number;
    errorDocument?: ApiErrorDocument;
  }) => void;
  onError?: (input: {
    request: HttpClientRequest;
    url: string;
    stage: 'network' | 'http' | 'response_parse';
    statusCode?: number;
    errorDocument?: ApiErrorDocument;
    error: Error;
    requestMetadata: BrowserHttpRequestMetadata;
    responseContentType?: string | null;
  }) => void;
}

const IDEMPOTENCY_IN_PROGRESS_RETRY_DELAYS_MS = [250, 750, 1500, 3000];
const PUBLIC_GUEST_ACCESS_SENTINEL = '__public__';

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function isNetworkUnavailableError(
  error: unknown,
): error is NetworkUnavailableError {
  return error instanceof NetworkUnavailableError;
}

export const offlineCacheResponseMetaSymbol = Symbol.for(
  'tov-plus.offline-cache-response-meta',
);

export interface OfflineCacheResponseMeta {
  key: string;
  savedAt: string;
  staleAfter?: string;
  isStale: boolean;
  app: HttpClientOfflineCacheResolvedScope['app'];
  occasionId?: string;
  actorId?: string;
  userId?: string;
}

export function getOfflineCacheResponseMeta(
  value: unknown,
): OfflineCacheResponseMeta | null {
  if (
    (typeof value !== 'object' && typeof value !== 'function') ||
    value === null
  ) {
    return null;
  }

  return (
    (value as { [offlineCacheResponseMetaSymbol]?: OfflineCacheResponseMeta })[
      offlineCacheResponseMetaSymbol
    ] ?? null
  );
}

export function copyOfflineCacheResponseMeta<T>(source: unknown, target: T): T {
  const meta = getOfflineCacheResponseMeta(source);

  if (
    !meta ||
    (typeof target !== 'object' && typeof target !== 'function') ||
    target === null
  ) {
    return target;
  }

  Object.defineProperty(target, offlineCacheResponseMetaSymbol, {
    configurable: true,
    enumerable: false,
    value: meta,
  });

  return target;
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: HttpClientRequest['query'],
): string {
  const url = new URL(path, `${baseUrl}/`);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (key === 'guest_token' && value !== PUBLIC_GUEST_ACCESS_SENTINEL) {
      continue;
    }

    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

function resolveRequestHeaders(
  request: HttpClientRequest,
): Record<string, string> {
  const headers = { ...(request.headers ?? {}) };
  const guestToken = request.query?.guest_token;

  if (
    guestToken !== undefined &&
    guestToken !== PUBLIC_GUEST_ACCESS_SENTINEL &&
    !headers['x-tov-guest-token'] &&
    !headers.Authorization
  ) {
    headers['x-tov-guest-token'] = String(guestToken);
  }

  return headers;
}

function hasHeader(headers: Record<string, string>, headerName: string) {
  const normalizedHeaderName = headerName.toLowerCase();

  return Object.keys(headers).some(
    (currentHeaderName) =>
      currentHeaderName.toLowerCase() === normalizedHeaderName,
  );
}

function createRandomId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `req_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2)}`;
}

async function parseErrorDocument(
  response: Response,
): Promise<ApiErrorDocument | undefined> {
  const contentType = response.headers.get('content-type');

  if (!contentType || !contentType.includes('application/json')) {
    return undefined;
  }

  const payload = (await response.json()) as Partial<ApiErrorDocument>;

  if (
    payload.error?.code &&
    payload.error?.message &&
    payload.error?.request_id
  ) {
    return payload as ApiErrorDocument;
  }

  return undefined;
}

function isBrowserOffline() {
  return (
    typeof navigator !== 'undefined' &&
    'onLine' in navigator &&
    navigator.onLine === false
  );
}

function isFetchNetworkFailure(error: unknown) {
  return (
    error instanceof TypeError ||
    (error instanceof Error &&
      /failed to fetch|networkerror|load failed|network request failed/i.test(
        error.message,
      ))
  );
}

async function hashGuestToken(guestToken: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(guestToken);

  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', data);

    return Array.from(new Uint8Array(digest))
      .map((value) => value.toString(16).padStart(2, '0'))
      .join('');
  }

  let hash = 2166136261;

  for (const character of guestToken) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }

  return `fnv1a:${(hash >>> 0).toString(16)}`;
}

async function resolveOfflineCacheScope(
  request: HttpClientRequest,
): Promise<HttpClientOfflineCacheResolvedScope | null> {
  if (!request.offlineCache) {
    return null;
  }

  const { guestToken, ...scope } = request.offlineCache.scope;

  return {
    ...scope,
    ...(guestToken ? { guestTokenHash: await hashGuestToken(guestToken) } : {}),
  };
}

function attachOfflineCacheMeta<T>(
  data: T,
  result: HttpClientOfflineCacheReadResult<T>,
): T {
  if (
    (typeof data !== 'object' && typeof data !== 'function') ||
    data === null
  ) {
    return data;
  }

  Object.defineProperty(data, offlineCacheResponseMetaSymbol, {
    configurable: true,
    enumerable: false,
    value: {
      key: result.entry.key,
      savedAt: result.entry.savedAt,
      staleAfter: result.entry.staleAfter,
      isStale: result.isStale,
      app: result.entry.app,
      occasionId: result.entry.occasionId,
      actorId: result.entry.actorId,
      userId: result.entry.userId,
    } satisfies OfflineCacheResponseMeta,
  });

  return data;
}

export function createBrowserHttpClient(
  options: BrowserHttpClientOptions,
): HttpClient {
  const fetchImplementation = options.fetchImplementation ?? fetch;
  const generateRequestId = options.generateRequestId ?? createRandomId;
  const generateIdempotencyKey =
    options.generateIdempotencyKey ?? createRandomId;

  return {
    async request<TResponse>(request: HttpClientRequest): Promise<TResponse> {
      const url = buildUrl(options.baseUrl, request.path, request.query);
      const offlineCacheScope = await resolveOfflineCacheScope(request);
      const resolvedDefaultHeaders =
        typeof options.defaultHeaders === 'function'
          ? options.defaultHeaders(request)
          : options.defaultHeaders;
      const requestHeaders = resolveRequestHeaders(request);
      const requestTimeoutMs = request.timeoutMs ?? options.requestTimeoutMs;
      const timeoutEnabled =
        typeof requestTimeoutMs === 'number' &&
        requestTimeoutMs > 0 &&
        typeof AbortController !== 'undefined';
      const requestId = request.requestId ?? generateRequestId();
      const idempotencyKey =
        request.method !== 'GET' && request.idempotencyKey
          ? request.idempotencyKey === 'auto'
            ? generateIdempotencyKey()
            : request.idempotencyKey
          : undefined;
      const requestMetadata: BrowserHttpRequestMetadata = {
        requestId,
        ...(idempotencyKey ? { idempotencyKey } : {}),
      };
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...(request.body ? { 'Content-Type': 'application/json' } : {}),
        ...resolvedDefaultHeaders,
        ...requestHeaders,
      };

      if (!hasHeader(headers, 'x-request-id')) {
        headers['x-request-id'] = requestId;
      }

      if (idempotencyKey && !hasHeader(headers, 'x-idempotency-key')) {
        headers['x-idempotency-key'] = idempotencyKey;
      }

      await options.beforeRequest?.({
        request,
        url,
        requestMetadata,
      });

      let timeoutRetryRemaining =
        Boolean(request.retryOnTimeout) && Boolean(idempotencyKey) ? 1 : 0;
      let inProgressRetryIndex = 0;
      let expiredJwtRetryRemaining = options.beforeRequest ? 1 : 0;
      let response: Response;

      requestAttempt: while (true) {
        while (true) {
          const abortController = timeoutEnabled ? new AbortController() : null;
          let timeoutId: ReturnType<typeof setTimeout> | null = null;
          let timeoutTriggered = false;

          try {
            if (abortController && requestTimeoutMs) {
              timeoutId = setTimeout(() => {
                timeoutTriggered = true;
                abortController.abort();
              }, requestTimeoutMs);
            }

            response = await fetchImplementation(url, {
              method: request.method,
              credentials: 'include',
              signal: abortController?.signal,
              headers,
              body: request.body ? JSON.stringify(request.body) : undefined,
            });
            break;
          } catch (error) {
            if (timeoutTriggered && timeoutRetryRemaining > 0) {
              timeoutRetryRemaining -= 1;
              continue;
            }

            const normalizedError =
              error instanceof NetworkUnavailableError
                ? error
                : timeoutTriggered
                  ? new NetworkUnavailableError(
                      `Browser request to ${request.path} timed out after ${requestTimeoutMs}ms.`,
                      'timeout',
                      error,
                      requestMetadata,
                    )
                  : isFetchNetworkFailure(error)
                    ? new NetworkUnavailableError(
                        'Cannot verify your session while offline. Reconnect to continue.',
                        isBrowserOffline() ? 'browser_offline' : 'fetch_failed',
                        error,
                        requestMetadata,
                      )
                    : error instanceof Error
                      ? error
                      : new Error(
                          'Browser request failed before a response was received.',
                        );
            if (normalizedError instanceof NetworkUnavailableError) {
              normalizedError.requestMetadata = requestMetadata;
            }
            options.onError?.({
              request,
              url,
              stage: 'network',
              error: normalizedError,
              requestMetadata,
            });

            if (
              request.method === 'GET' &&
              request.offlineCache?.allowStaleWhenOffline &&
              offlineCacheScope &&
              options.offlineCacheAdapter &&
              normalizedError instanceof NetworkUnavailableError
            ) {
              try {
                const cached =
                  await options.offlineCacheAdapter.read<TResponse>(
                    request.offlineCache.key,
                    offlineCacheScope,
                  );

                if (cached) {
                  return attachOfflineCacheMeta(cached.entry.data, cached);
                }
              } catch {
                // Offline cache is a best-effort fallback. Storage can fail in
                // browser-specific ways, but the caller still needs the original
                // network error so session gates render the offline state.
              }
            }

            throw normalizedError;
          } finally {
            if (timeoutId !== null) {
              clearTimeout(timeoutId);
            }
          }
        }

        if (!response.ok) {
          let errorDocument: ApiErrorDocument | undefined;

          try {
            errorDocument = await parseErrorDocument(response);
          } catch (error) {
            const normalizedError =
              error instanceof Error
                ? error
                : new Error('Failed to parse the browser API error response.');
            options.onError?.({
              request,
              url,
              stage: 'response_parse',
              statusCode: response.status,
              error: normalizedError,
              requestMetadata,
              responseContentType: response.headers.get('content-type'),
            });
            throw normalizedError;
          }

          if (
            response.status === 409 &&
            errorDocument?.error.code === 'request_in_progress' &&
            request.retryOnTimeout &&
            idempotencyKey &&
            inProgressRetryIndex <
              IDEMPOTENCY_IN_PROGRESS_RETRY_DELAYS_MS.length
          ) {
            const retryDelayMs =
              IDEMPOTENCY_IN_PROGRESS_RETRY_DELAYS_MS[inProgressRetryIndex] ??
              3000;
            inProgressRetryIndex += 1;
            await sleep(retryDelayMs);
            continue requestAttempt;
          }

          if (
            expiredJwtRetryRemaining > 0 &&
            isExpiredJwtAuthFailure({
              statusCode: response.status,
              code: errorDocument?.error.code,
              message: errorDocument?.error.message,
            })
          ) {
            expiredJwtRetryRemaining -= 1;
            await options.beforeRequest?.({
              request,
              url,
              requestMetadata,
            });
            continue requestAttempt;
          }

          const message =
            errorDocument?.error.message ??
            `Request to ${request.path} failed with status ${response.status}.`;
          const browserHttpError = new BrowserHttpError(
            message,
            response.status,
            errorDocument,
            requestMetadata,
          );

          if (response.status === 401) {
            options.onUnauthorized?.({
              request,
              statusCode: response.status,
              errorDocument,
            });
          }

          options.onError?.({
            request,
            url,
            stage: 'http',
            statusCode: response.status,
            errorDocument,
            error: browserHttpError,
            requestMetadata,
            responseContentType: response.headers.get('content-type'),
          });

          throw browserHttpError;
        }

        break;
      }

      if (response.status === 204) {
        return undefined as TResponse;
      }

      try {
        const responseText = await response.text();

        if (responseText.trim().length === 0) {
          return undefined as TResponse;
        }

        const data = JSON.parse(responseText) as TResponse;

        if (
          request.method === 'GET' &&
          request.offlineCache &&
          offlineCacheScope &&
          options.offlineCacheAdapter
        ) {
          const savedAt = new Date();
          const staleAfter =
            typeof request.offlineCache.staleAfterMs === 'number'
              ? new Date(
                  savedAt.getTime() + request.offlineCache.staleAfterMs,
                ).toISOString()
              : undefined;

          try {
            await options.offlineCacheAdapter.write({
              key: request.offlineCache.key,
              data,
              staleAfter,
              ...offlineCacheScope,
            });
          } catch {
            // Offline caching is best-effort; successful online responses must
            // not fail because local durable storage is unavailable or full.
          }
        }

        return data;
      } catch (error) {
        const normalizedError =
          error instanceof Error
            ? error
            : new Error('Failed to parse the browser API response.');
        options.onError?.({
          request,
          url,
          stage: 'response_parse',
          statusCode: response.status,
          error: normalizedError,
          requestMetadata,
          responseContentType: response.headers.get('content-type'),
        });
        throw normalizedError;
      }
    },
  };
}
